import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { carradaFotoUrl } from "@/services/carradas";

export type CarradaFotoLightboxItem = {
  carradaId: string;
  fotoId: string;
  lado?: string;
};

type Props = {
  items: CarradaFotoLightboxItem[];
  /** Índice inicial ao abrir (padrão 0). */
  initialIndex?: number;
  onClose: () => void;
};

export function CarradaFotoLightbox({
  items,
  initialIndex = 0,
  onClose,
}: Props) {
  const total = items.length;
  const [index, setIndex] = useState(() =>
    total === 0 ? 0 : Math.min(Math.max(initialIndex, 0), total - 1),
  );

  useEffect(() => {
    if (total === 0) return;
    setIndex((prev) => Math.min(Math.max(prev, 0), total - 1));
  }, [total]);

  const goPrev = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i <= 0 ? total - 1 : i - 1));
  }, [total]);

  const goNext = useCallback(() => {
    if (total <= 1) return;
    setIndex((i) => (i >= total - 1 ? 0 : i + 1));
  }, [total]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  if (total === 0) return null;

  const current = items[index];
  const hasNav = total > 1;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="relative flex max-h-[90vh] max-w-[90vw] items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Fechar"
          className="absolute -right-2 -top-2 z-10 rounded-full bg-black/70 p-1.5 text-white shadow hover:bg-black/90"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>

        {hasNav && (
          <button
            type="button"
            aria-label="Foto anterior"
            className="absolute left-2 z-10 rounded-full bg-black/60 p-2 text-white shadow hover:bg-black/80 md:-left-14"
            onClick={goPrev}
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
        )}

        <img
          src={carradaFotoUrl(current.carradaId, current.fotoId)}
          alt={`Foto da caçamba ${index + 1} de ${total}`}
          className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-xl"
        />

        {hasNav && (
          <button
            type="button"
            aria-label="Próxima foto"
            className="absolute right-2 z-10 rounded-full bg-black/60 p-2 text-white shadow hover:bg-black/80 md:-right-14"
            onClick={goNext}
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        )}

        {(hasNav || current.lado) && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-black/65 px-3 py-1 text-xs font-medium text-white tabular-nums">
            {current.lado ? `${current.lado} · ` : ""}
            {index + 1} / {total}
          </div>
        )}
      </div>
    </div>
  );
}
